import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Mic, Pause, Play, Square, Trash2, X, Heart, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { api, errMsg, getGuestName, setGuestName } from "@/lib/api";
import { inputCls } from "@/pages/Login";

const fmt = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
function pickMime() {
  if (!window.MediaRecorder) return null;
  for (const t of ["audio/webm;codecs=opus", "audio/mp4", "audio/webm", "audio/ogg;codecs=opus"]) if (MediaRecorder.isTypeSupported?.(t)) return t;
  return "";
}
const extFor = (mime) => (mime.includes("mp4") ? "m4a" : mime.includes("ogg") ? "ogg" : mime.includes("wav") ? "wav" : mime.includes("mpeg") ? "mp3" : "webm");

export default function VoiceRecorder({ open, onClose, slug, wedding, token }) {
  const max = wedding.voice_max_seconds || 60;
  const [step, setStep] = useState("intro");
  const [name, setName] = useState(getGuestName());
  const [elapsed, setElapsed] = useState(0);
  const [paused, setPaused] = useState(false);
  const [blob, setBlob] = useState(null);
  const [error, setError] = useState("");
  const rec = useRef(null);
  const chunks = useRef([]);
  const timer = useRef(null);
  const acc = useRef({ base: 0, start: 0 });
  const url = blob ? URL.createObjectURL(blob) : null;
  const supported = !!(navigator.mediaDevices?.getUserMedia && pickMime() !== null);

  useEffect(() => () => url && URL.revokeObjectURL(url), [url]);
  useEffect(() => { if (!open) reset(); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const tick = () => {
    const s = acc.current.base + (Date.now() - acc.current.start) / 1000;
    setElapsed(s);
    if (s >= max) stop();
  };
  const start = async () => {
    setError("");
    if (name.trim().length < 2) return setError("Vpiši ime in priimek.");
    setGuestName(name);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
      const mime = pickMime();
      const r = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunks.current = [];
      r.ondataavailable = (e) => e.data.size && chunks.current.push(e.data);
      r.onstop = () => { stream.getTracks().forEach((t) => t.stop()); setBlob(new Blob(chunks.current, { type: r.mimeType || mime || "audio/webm" })); setStep("review"); };
      r.start(1000);
      rec.current = r;
      acc.current = { base: 0, start: Date.now() };
      timer.current = setInterval(tick, 200);
      setPaused(false);
      setStep("recording");
    } catch (e) {
      setError("Dostop do mikrofona ni bil dovoljen. Omogoči mikrofon v nastavitvah brskalnika ali izberi posnetek spodaj.");
    }
  };
  const pause = () => {
    const r = rec.current;
    if (!r?.pause) return;
    if (r.state === "recording") { r.pause(); acc.current.base += (Date.now() - acc.current.start) / 1000; clearInterval(timer.current); setPaused(true); }
    else if (r.state === "paused") { r.resume(); acc.current.start = Date.now(); timer.current = setInterval(tick, 200); setPaused(false); }
  };
  function stop() { clearInterval(timer.current); if (rec.current && rec.current.state !== "inactive") rec.current.stop(); }
  function reset() { stop(); setBlob(null); setElapsed(0); setStep("intro"); setError(""); }
  const pickFile = (e) => { const f = e.target.files[0]; if (f) { if (name.trim().length < 2) return setError("Vpiši ime in priimek."); setGuestName(name); setBlob(f); setStep("review"); } };
  const send = async () => {
    setStep("sending");
    const fd = new FormData();
    fd.append("file", blob, `glasovno.${extFor(blob.type || "")}`);
    fd.append("name", name.trim());
    fd.append("guest_token", token);
    try { await api.post(`/public/w/${slug}/voice`, fd); setStep("done"); }
    catch (e) { setError(errMsg(e, "Pošiljanje ni uspelo. Poskusi znova.")); setStep("review"); }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center" data-testid="voice-recorder">
          <motion.div initial={{ y: 40 }} animate={{ y: 0 }} exit={{ y: 40 }} className="w-full sm:max-w-md bg-[#FAF7F2] rounded-t-3xl sm:rounded-3xl p-6 pb-10 space-y-6 max-h-[92vh] overflow-y-auto">
            <div className="flex justify-between items-start">
              <div><p className="eyebrow">Glasovna knjiga gostov</p><h2 className="font-display text-3xl mt-1">Pusti glasovno sporočilo</h2></div>
              <button onClick={onClose} className="p-2 rounded-full hover:bg-[#EADBCE]" data-testid="voice-close" aria-label="Zapri"><X size={20} /></button>
            </div>
            {step === "intro" && (
              <div className="space-y-4">
                <label className="block text-sm text-[#6C6560]">Ime in priimek<input className={`${inputCls} mt-1`} value={name} onChange={(e) => setName(e.target.value)} placeholder="Janez Novak" data-testid="voice-name-input" /></label>
                <p className="text-xs text-[#6C6560] bg-[#F3ECE1] rounded-xl p-3" data-testid="voice-privacy-notice">Tvoje sporočilo slišita le mladoporočenca (in skrbnik platforme), razen če se odločita knjigo deliti z gosti. Največja dolžina: {max} sekund. <Link to="/pravno/zasebnost" target="_blank" className="underline">Zasebnost</Link></p>
                {supported ? (
                  <button onClick={start} className="mx-auto w-28 h-28 rounded-full bg-[#231F20] text-white grid place-items-center shadow-xl" data-testid="voice-start-button" aria-label="Začni snemanje"><Mic size={40} /></button>
                ) : <p className="text-sm text-center">Tvoj brskalnik ne podpira snemanja. Izberi posnetek spodaj.</p>}
                {supported && <p className="text-center text-sm text-[#6C6560]">Pritisni in začni govoriti</p>}
                <label className="block text-center text-xs underline text-[#6C6560] cursor-pointer" data-testid="voice-file-fallback">ali izberi zvočni posnetek<input type="file" accept="audio/*" capture className="hidden" onChange={pickFile} data-testid="voice-file-input" /></label>
              </div>
            )}
            {step === "recording" && (
              <div className="text-center space-y-6">
                <div className={`mx-auto w-32 h-32 rounded-full grid place-items-center ${paused ? "bg-[#DFD5C5]" : "bg-[#C99A8E] animate-pulse"}`}><Mic size={44} className="text-white" /></div>
                <p className="font-display text-5xl" data-testid="voice-timer">{fmt(elapsed)} <span className="text-xl text-[#968E87]">/ {fmt(max)}</span></p>
                <div className="flex justify-center gap-3">
                  {rec.current?.pause && <button onClick={pause} className="btn-line px-5 h-12 flex items-center gap-2" data-testid="voice-pause-button">{paused ? <><Play size={16} /> Nadaljuj</> : <><Pause size={16} /> Premor</>}</button>}
                  <button onClick={stop} className="btn-dark px-6 h-12 flex items-center gap-2" data-testid="voice-stop-button"><Square size={16} /> Ustavi</button>
                </div>
              </div>
            )}
            {(step === "review" || step === "sending") && (
              <div className="space-y-4">
                <audio src={url} controls className="w-full" data-testid="voice-preview-audio" />
                <div className="grid grid-cols-2 gap-3">
                  <button onClick={reset} disabled={step === "sending"} className="btn-line h-12 flex items-center justify-center gap-2" data-testid="voice-rerecord-button"><Trash2 size={16} /> Posnemi znova</button>
                  <button onClick={send} disabled={step === "sending"} className="btn-dark h-12 flex items-center justify-center gap-2" data-testid="voice-send-button">{step === "sending" ? <Loader2 size={16} className="animate-spin" /> : null} Pošlji</button>
                </div>
              </div>
            )}
            {step === "done" && (
              <div className="text-center space-y-4 py-4" data-testid="voice-done">
                <Heart size={40} className="mx-auto text-[#C99A8E] fill-[#E8B4A8]" />
                <p className="font-display text-3xl">Hvala za tvoje sporočilo!</p>
                <button onClick={onClose} className="btn-dark px-8 h-12" data-testid="voice-done-close">Zapri</button>
              </div>
            )}
            {error && <p className="text-sm text-red-700" data-testid="voice-error">{error}</p>}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
